// FCP design system — Theme toggle (v2.8.5 redesign)
//
// Was: single button cycling light → dark → auto, hard to tell which was
// active without inspecting a tiny dot indicator.
//
// Now: segmented 3-state pill. All three options visible at once, click
// any to set directly. Active option gets a raised background + brand
// accent. Auto mode shows a small "system: light/dark" hint via title.

import React, { useRef } from 'react';
import { useTheme } from '../hooks/useTheme';
import './ThemeToggle.css';

const SunIcon = () => (
  <svg viewBox="0 0 20 20" width="14" height="14" fill="none" aria-hidden="true">
    <circle cx="10" cy="10" r="3.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.2 4.2l1.4 1.4M14.4 14.4l1.4 1.4M4.2 15.8l1.4-1.4M14.4 5.6l1.4-1.4"
          stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const MoonIcon = () => (
  <svg viewBox="0 0 20 20" width="14" height="14" fill="none" aria-hidden="true">
    <path d="M16 11.5A6.5 6.5 0 0 1 8.5 4a.5.5 0 0 0-.65-.5A7.5 7.5 0 1 0 16.5 12a.5.5 0 0 0-.5-.5z"
          stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
  </svg>
);

const AutoIcon = () => (
  <svg viewBox="0 0 20 20" width="14" height="14" fill="none" aria-hidden="true">
    {/* Half-filled circle — visually says "follows the environment" */}
    <circle cx="10" cy="10" r="6.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M10 3.5a6.5 6.5 0 0 0 0 13z" fill="currentColor" />
  </svg>
);

const OPTIONS = [
  { id: 'light', icon: SunIcon,  label: 'Light' },
  { id: 'dark',  icon: MoonIcon, label: 'Dark' },
  { id: 'auto',  icon: AutoIcon, label: 'Auto' },
];

export function ThemeToggle({ className = '' }) {
  const { mode, resolvedMode, setMode } = useTheme();
  const refs = useRef([]);

  // The index the tab key lands on. A radiogroup is ONE tab stop, not three —
  // the checked option is the entry point and the arrows move between them.
  const activeIndex = OPTIONS.findIndex(o => o.id === mode);
  const tabIndexOf = (i) => (i === (activeIndex === -1 ? 0 : activeIndex) ? 0 : -1);

  /**
   * Arrow keys, because `role="radiogroup"` PROMISES them (#300 §1).
   *
   * The role was here from the redesign and the behaviour never was: three
   * plain buttons, all three in the tab order, no key handling at all. A
   * screen reader announces "radio group, 1 of 3" and tells the person to use
   * the arrows — and the arrows did nothing, while tab moved through all three
   * as if they were separate controls. That is worse than a plain button
   * group, because the announcement is a promise the widget does not keep.
   *
   * Selecting on arrow, not merely moving focus, is the standard radiogroup
   * behaviour and is the right one here: every option is applied instantly and
   * reversibly, so there is nothing to confirm. Home/End are part of the same
   * pattern and cost two lines.
   *
   * Focus is moved imperatively on the node that is already mounted rather
   * than through an effect: `setMode` re-renders and the roving tabIndex
   * follows, but nothing would move the CARET without this.
   */
  const onKeyDown = (e, i) => {
    const last = OPTIONS.length - 1;
    let next = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = i === last ? 0 : i + 1;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = i === 0 ? last : i - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next === null) return;
    e.preventDefault();          // arrows must not scroll the page as well
    setMode(OPTIONS[next].id);
    const el = refs.current[next];
    if (el && typeof el.focus === 'function') el.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className={`fcp-theme-toggle ${className}`}
    >
      {OPTIONS.map((opt, i) => {
        const Icon = opt.icon;
        const isActive = mode === opt.id;
        const title = opt.id === 'auto'
          ? `Auto (currently ${resolvedMode})`
          : opt.label;
        return (
          <button
            key={opt.id}
            ref={(el) => { refs.current[i] = el; }}
            type="button"
            role="radio"
            aria-checked={isActive}
            tabIndex={tabIndexOf(i)}
            className={`fcp-theme-toggle__opt ${isActive ? 'is-active' : ''}`}
            onClick={() => setMode(opt.id)}
            onKeyDown={(e) => onKeyDown(e, i)}
            title={title}
            aria-label={title}
          >
            <Icon />
            <span className="fcp-theme-toggle__label">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export default ThemeToggle;
